import {
  type CameraScanPhase,
  type CameraScanProgress,
  type CameraScanTelemetry,
  CameraScanner,
  type ImageDecodeOutcome,
  ScanGuideInsets,
  decodePdf417FromImage,
  isDecodableImageFile,
  isStreamWidthReliable,
  requestRearCameraStream,
  stopCameraStream,
} from '@dutchie/capacitor-hardware';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';

import type { IdDocumentType } from '@dutchie-pos/requests/checkin/typed-id-entry';

import CameraIcon from 'src/assets/camera.svg?react';
import { ImageIcon } from 'src/assets/image-icon';
import { DutchieButton } from 'src/components/dutchie-button';
import { colors } from 'src/css/theme';

import {
  ActionIcon,
  Actions,
  CameraOverlay,
  Container,
  Hint,
  OverlayActions,
  OverlayFrame,
  OverlayVideo,
  Prompt,
} from '../step-chrome';
import {
  CAMERA_LOW_RESOLUTION_MESSAGE,
  CAMERA_ROTATE_MESSAGE,
  ID_SCAN_GUIDE_HINT,
  type ScanGuideStatus,
  cameraErrorMessage,
  scanStatus,
} from './camera-copy';
import { preparePdf417Decoder } from './pdf417-wasm';

export type CameraScanResult = {
  barcode: { text: string; bytes: Uint8Array } | null;
  telemetry: CameraScanTelemetry;
};

/** Three states, one button: idle, decoding frames, and waiting on the check-in. */
const captureLabel = (isEntering: boolean, isScanning: boolean): string => {
  if (isEntering) {
    return 'Processing ID';
  }

  return isScanning ? 'Scanning' : 'Use camera';
};

type CaptureStepProps = {
  isEntering: boolean;
  /**
   * Which document is in staff's hands. The decode itself does not use it — the server parses the
   * card — but a read that fails falls through to hand entry, and that path has to know which
   * identification slot the number belongs in. Asked here because this is where the card is in
   * front of the operator, and it survives into the typed form rather than being asked twice.
   */
  documentType: IdDocumentType;
  onScanResult: (result: CameraScanResult) => void;
  onUploadResult: (result: ImageDecodeOutcome) => void;
  onCameraError: (message: string) => void;
  onDocumentTypeChange: (documentType: IdDocumentType) => void;
};

/** No shutter: the decode is the shutter, read off the live preview frame by frame. */
export const CaptureStep: FC<CaptureStepProps> = ({
  isEntering,
  documentType,
  onScanResult,
  onUploadResult,
  onCameraError,
  onDocumentTypeChange,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const uploadRef = useRef<HTMLInputElement | null>(null);
  // This step owns the stream and stops it on every exit; CameraScanner never stops tracks it adopted.
  const streamRef = useRef<MediaStream | null>(null);
  const isOpening = useRef(false);
  /**
   * Which camera-open attempt is current. Bumped the moment *any* upload starts, and snapshotted by
   * `useCamera` before it awaits the permission prompt, so a grant that resolves against a stale
   * generation is discarded.
   *
   * This replaces an `isUploading` flag that was only true while the image was being decoded. The
   * decode is the short part: `handleUpload` clears it as soon as `onUploadResult` returns, which is
   * the exact moment the parent starts the check-in. So the operator could tap Use camera, pick a
   * file while the prompt was still open, have the local decode finish first — fast on a clean
   * image — and then have the grant sail through the guard and open the scanning overlay *behind an
   * entry already in flight*. A frame decoding there would hand up a second result for the same
   * guest. A generation cannot close like that: once an upload has happened, this attempt is
   * invalid for good, however long the prompt sits.
   *
   * Not `isEntering`: that prop is captured at the render `useCamera` closed over, so it is stale by
   * the time the await resolves, and reading it through a ref would still only cover the parent's
   * request rather than the whole upload.
   */
  const attemptGeneration = useRef(0);
  const isMounted = useRef(true);
  const [isScanning, setIsScanning] = useState(false);
  const [isOpeningCamera, setIsOpeningCamera] = useState(false);
  const [isReadingImage, setIsReadingImage] = useState(false);
  const [scanPhase, setScanPhase] = useState<CameraScanPhase | null>(null);
  // Said inside the overlay, where the operator is looking: turning the phone is the only thing that
  // changes a portrait stream's width, because no amount of CSS adds pixels.
  const [advisory, setAdvisory] = useState<string | null>(null);
  const status: ScanGuideStatus = scanStatus(scanPhase);

  /** Only a phase change re-renders: the scanner's own throttle keeps this off the frame path. */
  const handleProgress = useCallback((progress: CameraScanProgress) => {
    setScanPhase((current) => (current === progress.phase ? current : progress.phase));
  }, []);

  const releaseStream = useCallback(() => {
    stopCameraStream(streamRef.current);
    streamRef.current = null;
    setIsOpeningCamera(false);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Fetched on mount, not on the button: on iOS the wasm is the only decoder and weighs ~931 KB.
  useEffect(preparePdf417Decoder, []);

  // The parent's handler, held in a ref and read at call time rather than closed over.
  //
  // This is the fix for a real abort: `onScanResult` is a fresh closure on every parent render, and
  // the parent re-renders while a scan is running — the entry mutation going pending, a toast, a
  // step change. Depending on it made `handleResult` a new function each time, which changed the
  // scan effect's identity, so React tore it down and ran it again: `abort.abort()` mid-decode, a
  // new `CameraScanner` from scratch, and every frame read so far thrown away. On a phone that is
  // the difference between reading a dense PDF417 and never settling at all.
  //
  // Declared before the scan effect, so the ref is already current by the time it runs.
  const latestScanResult = useRef(onScanResult);
  useEffect(() => {
    latestScanResult.current = onScanResult;
  }, [onScanResult]);

  /** Stable for the component's life, so the scan effect below depends only on the session inputs. */
  const handleResult = useCallback(
    (result: CameraScanResult) => {
      releaseStream();
      setIsScanning(false);
      latestScanResult.current(result);
    },
    [releaseStream],
  );

  // Set on every run, not just the first: StrictMode mounts, tears down and remounts, and a
  // cleanup-only version left this false for the rest of the component's life — silently discarding a
  // granted stream and swallowing a refusal. Unmount releases the stream, or the camera stays lit.
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      releaseStream();
    };
  }, [releaseStream]);

  // Opened under the click, which iOS Safari requires. The prompt can sit for seconds, so a second tap
  // or a left screen must not strand a live stream; a ref, because state would not settle in time.
  const useCamera = async () => {
    if (isOpening.current || streamRef.current) {
      return;
    }

    isOpening.current = true;
    setIsOpeningCamera(true);
    setScanPhase(null);
    setAdvisory(null);
    // Taken before the await, so anything that invalidates this attempt while the prompt is open is
    // visible as a changed value afterwards.
    const generation = attemptGeneration.current;
    try {
      const result = await requestRearCameraStream();

      // Too late to be wanted: the screen was left, a stream is already held, or an upload took over
      // at any point since this attempt began — including one whose decode has already finished and
      // whose check-in is now in flight.
      if (!isMounted.current || streamRef.current || attemptGeneration.current !== generation) {
        stopCameraStream(result.stream);
        return;
      }
      if (!result.stream) {
        onCameraError(cameraErrorMessage(result.error));
        return;
      }

      const settings = result.stream.getVideoTracks()[0]?.getSettings();
      const width = settings?.width ?? 0;
      const height = settings?.height ?? 0;
      if (width > 0 && !isStreamWidthReliable(width)) {
        // Portrait is the actionable case: the same camera turned sideways is wide enough.
        const portrait = height > width;
        setAdvisory(portrait ? CAMERA_ROTATE_MESSAGE : CAMERA_LOW_RESOLUTION_MESSAGE);
      } else {
        setAdvisory(null);
      }

      // The effect attaches and plays it: the preview is display:none until isScanning flips, and a
      // hidden video reports no dimensions, so decoding it would spin on empty frames.
      streamRef.current = result.stream;
      setIsScanning(true);
    } finally {
      // Always, or one throw leaves the actions disabled with only a refresh to clear it.
      isOpening.current = false;
      if (isMounted.current) {
        setIsOpeningCamera(false);
      }
    }
  };

  const cancelScan = () => {
    releaseStream();
    setIsScanning(false);
  };

  /** Decoded locally; the image itself is never uploaded. */
  const handleUpload = async (file: File) => {
    // Before the decodable check, and before anything can await: picking a file at all is what makes
    // a camera attempt opened beside it unwanted, whatever the file turns out to be.
    attemptGeneration.current += 1;

    if (!isDecodableImageFile(file)) {
      onUploadResult({ barcode: null, decoder: null, unsupported: true, elapsedMs: 0, widthPx: 0 });
      return;
    }

    setIsReadingImage(true);
    try {
      onUploadResult(await decodePdf417FromImage(file));
    } catch {
      // A decoder that throws is an image we could not read, which is what the copy already says.
      onUploadResult({ barcode: null, decoder: null, unsupported: true, elapsedMs: 0, widthPx: 0 });
    } finally {
      setIsReadingImage(false);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!isScanning || !video || !stream) {
      return undefined;
    }

    // Attached here rather than on the click: by now the preview is visible and has dimensions.
    video.srcObject = stream;
    void video.play().catch(() => undefined);

    const scanner = new CameraScanner();
    const abort = new AbortController();

    scanner
      .scan(video, { signal: abort.signal, onProgress: handleProgress })
      // Aborted means the step was left or cancelled, so the result has nowhere to go.
      .then((result) => (abort.signal.aborted ? undefined : handleResult(result)))
      .catch(() => undefined);

    return () => {
      abort.abort();
      void scanner.doDisconnect();
    };
    // `handleResult` and `handleProgress` are stable by construction, so in practice this runs on
    // `isScanning` alone: one attempt per session, surviving every parent re-render.
  }, [isScanning, handleResult, handleProgress]);

  return (
    <Container>
      <Prompt>Scan with a camera</Prompt>
      <Hint>{ID_SCAN_GUIDE_HINT}</Hint>
      {/* Hidden while the preview is up: the choice is made before scanning, and a control behind a
          fullscreen camera is only there to be missed. A native radio group, which brings keyboard
          and screen-reader behaviour styled buttons do not. */}
      {!isScanning && (
        <DocumentTypeFieldset>
          <DocumentTypeLegend>Which document is it?</DocumentTypeLegend>
          <DocumentTypeGroup>
            <DocumentTypeOption $selected={documentType === 'DriverLicense'}>
              <DocumentTypeInput
                checked={documentType === 'DriverLicense'}
                data-dd-action-name="Choose driver's licence (check in)"
                data-testid='check-in_document-type-license'
                name='check-in-document-type'
                type='radio'
                value='DriverLicense'
                onChange={() => onDocumentTypeChange('DriverLicense')}
              />
              Driver&rsquo;s licence
            </DocumentTypeOption>
            <DocumentTypeOption $selected={documentType === 'MedicalCard'}>
              <DocumentTypeInput
                checked={documentType === 'MedicalCard'}
                data-dd-action-name='Choose medical card (check in)'
                data-testid='check-in_document-type-medical'
                name='check-in-document-type'
                type='radio'
                value='MedicalCard'
                onChange={() => onDocumentTypeChange('MedicalCard')}
              />
              Medical card
            </DocumentTypeOption>
          </DocumentTypeGroup>
        </DocumentTypeFieldset>
      )}
      {/* Fullscreen while scanning: inline at container width the 1080p preview rendered into ~330px. */}
      <CameraOverlay $visible={isScanning} data-testid='check-in_preview-overlay'>
        <OverlayFrame data-testid='check-in_preview-frame'>
          <OverlayVideo data-testid='check-in_preview' muted playsInline ref={videoRef} />
          <ScanGuide
            $tone={status.tone}
            data-testid='check-in_scan-guide'
            data-tone={status.tone}
            style={ScanGuideInsets}
          />
          {/* Announced, not shown: the guide's colour must not be the only signal. */}
          <ScanStatus aria-live='polite' data-testid='check-in_scan-status' role='status'>
            {status.label}
          </ScanStatus>
          {/* Replaces the hint rather than stacking: the advisory is the one worth reading. */}
          {!advisory && <ScanHint data-testid='check-in_scan-hint'>{ID_SCAN_GUIDE_HINT}</ScanHint>}
          {advisory && (
            <Advisory data-testid='check-in_scan-advisory' role='alert'>
              {advisory}
            </Advisory>
          )}
        </OverlayFrame>
        {isScanning && (
          <OverlayActions>
            <DutchieButton
              automationId='check-in_stop-scanning'
              ddActionName='Stop scanning (check in)'
              onClick={cancelScan}
            >
              Cancel
            </DutchieButton>
          </OverlayActions>
        )}
      </CameraOverlay>
      <Actions>
        <DutchieButton
          automationId='check-in_capture'
          ddActionName='Use camera (check in)'
          disabled={isEntering || isScanning || isReadingImage || isOpeningCamera}
          onClick={() => {
            useCamera().catch(() => onCameraError(cameraErrorMessage('unknown')));
          }}
        >
          <ActionIcon aria-hidden='true'>
            <CameraIcon />
          </ActionIcon>
          {captureLabel(isEntering, isScanning)}
        </DutchieButton>
        {!isScanning && (
          <DutchieButton
            automationId='check-in_upload'
            ddActionName='Upload image (check in)'
            disabled={isEntering || isReadingImage}
            secondary
            onClick={() => uploadRef.current?.click()}
          >
            <ActionIcon aria-hidden='true'>
              <ImageIcon height={20} width={20} />
            </ActionIcon>
            {isReadingImage ? 'Reading the image' : 'Upload image'}
          </DutchieButton>
        )}
      </Actions>
      {/* No `capture`: that jumps straight to the camera on iOS and defeats the upload action. */}
      <input
        accept='image/*'
        data-testid='check-in_upload-input'
        ref={uploadRef}
        style={{ display: 'none' }}
        type='file'
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so picking the same file again re-fires change.
          event.target.value = '';
          if (file) {
            void handleUpload(file);
          }
        }}
      />
    </Container>
  );
};

const guideColor = (tone: ScanGuideStatus['tone']): string => {
  switch (tone) {
    case 'found':
      return colors.success;
    case 'searching':
    case 'error':
      return colors.red;
    case 'neutral':
      return '#ffffff';
  }
};

// Insets come from `ScanGuideInsets` inline, so this box and the scanner's crop cannot drift apart.
const ScanGuide = styled.div<{ $tone: ScanGuideStatus['tone'] }>`
  border: 0.1875rem solid ${({ $tone }) => guideColor($tone)};
  border-radius: 0.375rem;
  box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.4);
  pointer-events: none;
  position: absolute;
  transition: border-color 120ms ease-out;
`;

// Screen-reader only, and clipped rather than `display: none`, which would drop it from the tree.
const ScanStatus = styled.p`
  border: 0;
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const ScanHint = styled.p`
  bottom: 0.75rem;
  color: #ffffff;
  font-size: 0.875rem;
  margin: 0;
  padding: 0 1rem;
  position: absolute;
  text-align: center;
  text-shadow: 0 0 0.5rem rgba(0, 0, 0, 1);
  width: 100%;
`;

/** Centred over the guide: it is the one thing worth reading when it is showing. */
const Advisory = styled.p`
  background: rgba(0, 0, 0, 0.82);
  border-radius: 0.5rem;
  color: #ffffff;
  font-size: 0.9375rem;
  font-weight: 600;
  left: 50%;
  margin: 0;
  max-width: 18rem;
  padding: 0.625rem 0.875rem;
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%);
`;

/** A native radio group: keyboard and screen-reader behaviour styled buttons do not bring. */
const DocumentTypeFieldset = styled.fieldset`
  border: none;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
`;

const DocumentTypeLegend = styled.legend`
  color: var(--color-gray-70);
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 0;
  text-transform: uppercase;
`;

/** One inset track holding both options, so they read as a single control rather than two buttons. */
const DocumentTypeGroup = styled.div`
  background-color: var(--color-gray-20);
  border-radius: 0.5rem;
  display: flex;
  gap: 0.25rem;
  padding: 0.25rem;
`;

/** Comfortably tappable, and shorter than the capture button so the hierarchy is visible. */
const DocumentTypeOption = styled.label<{ $selected: boolean }>`
  align-items: center;
  background-color: ${({ $selected }) => ($selected ? 'white' : 'transparent')};
  border-radius: 0.375rem;
  box-shadow: ${({ $selected }) => ($selected ? '0 1px 2px rgba(0, 0, 0, 0.16)' : 'none')};
  color: ${({ $selected }) => ($selected ? 'black' : 'var(--color-gray-70)')};
  cursor: pointer;
  display: flex;
  flex: 1;
  font-size: 0.9375rem;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  gap: 0.5rem;
  justify-content: center;
  min-height: 2.75rem;
  padding: 0 0.5rem;

  &:focus-within {
    outline: 2px solid var(--color-blue-60, #0b5cd5);
    outline-offset: 1px;
  }
`;

const DocumentTypeInput = styled.input`
  accent-color: var(--color-blue-60, #0b5cd5);
  cursor: pointer;
  margin: 0;
`;
